import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError } from '../../lib/api';
import SwimmerSearch from '../../components/admin/SwimmerSearch';
import { Waves, UserCheck, LogOut, Plus, Loader2, Users as UsersIcon } from 'lucide-react';

const Stat = ({ label, value, gradient, testid }) => (
  <Card className={`border-0 shadow-sm bg-gradient-to-r ${gradient} text-white`}>
    <CardContent className="p-4"><p className="text-white/80 text-xs">{label}</p><p className="text-2xl font-bold" data-testid={testid}>{value}</p></CardContent>
  </Card>
);

const AttendancePage = () => {
  const [stats, setStats] = useState(null);
  const [inPool, setInPool] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const [st, active, hist] = await Promise.all([
        api.get('/attendance/today-stats'),
        api.get('/attendance/today', { params: { checked_out: false } }),
        api.get('/attendance/today'),
      ]);
      setStats(st.data); setInPool(active.data); setHistory(hist.data);
    } catch (err) {
      toast({ title: 'Failed to load attendance', description: formatApiError(err), variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const checkIn = async (swimmer) => {
    setChecking(true);
    try {
      await api.post('/attendance/check-in', { swimmer_id: swimmer.id, attendance_type: 'swimmer', method: 'manual' });
      toast({ title: `${swimmer.name} checked in 🏊` });
      setCheckInOpen(false);
      load();
    } catch (err) {
      toast({ title: 'Check-in failed', description: formatApiError(err), variant: 'destructive' });
    } finally { setChecking(false); }
  };

  const checkOut = async (attId, name) => {
    try {
      await api.post(`/attendance/${attId}/check-out`, {});
      toast({ title: `${name} checked out` });
      load();
    } catch (err) { toast({ title: 'Check-out failed', description: formatApiError(err), variant: 'destructive' }); }
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="h-10 w-10 animate-spin text-blue-500" /></div>;

  return (
    <div data-testid="admin-attendance">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Attendance</h1>
          <p className="text-gray-500 text-sm mt-1">Live check-in / check-out tracking</p>
        </div>
        <Button onClick={() => setCheckInOpen(true)} className="bg-gradient-to-r from-cyan-600 to-teal-600" data-testid="checkin-btn">
          <Plus className="mr-2 h-4 w-4" /> Check In Member
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="In Pool Now" value={stats?.currently_in_pool ?? 0} gradient="from-teal-600 to-cyan-600" testid="att-in-pool" />
        <Stat label="Total Check-ins" value={stats?.total_checkins ?? 0} gradient="from-blue-600 to-indigo-600" testid="att-total" />
        <Stat label="Checked Out" value={stats?.checked_out ?? 0} gradient="from-gray-600 to-gray-700" testid="att-out" />
        <Stat label="Swimmers Today" value={stats?.swimmers ?? 0} gradient="from-purple-600 to-pink-600" testid="att-swimmers" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="shadow-md border-0 bg-white">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Waves className="h-5 w-5 text-cyan-500" /> Currently in Pool</CardTitle></CardHeader>
          <CardContent>
            {inPool.length === 0 ? <p className="text-sm text-gray-500 py-4">Nobody in the pool right now.</p> : (
              <div className="space-y-2" data-testid="in-pool-list">
                {inPool.map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                    <div className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-green-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{a.swimmer_name || a.user_name || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">In since {a.check_in_time?.slice(11, 16)} · {a.swimmer_code || ''}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => checkOut(a.id, a.swimmer_name || 'Member')} data-testid={`checkout-${a.id}`}>
                      <LogOut className="mr-1 h-3.5 w-3.5" /> Check Out
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-md border-0 bg-white">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><UsersIcon className="h-5 w-5" /> Today's Log</CardTitle></CardHeader>
          <CardContent>
            {history.length === 0 ? <p className="text-sm text-gray-500 py-4">No check-ins today.</p> : (
              <div className="space-y-2" data-testid="attendance-log">
                {history.map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0">
                    <span>{a.swimmer_name || a.user_name || 'Unknown'}</span>
                    <span className="text-gray-500 flex items-center gap-2">
                      {a.check_in_time?.slice(11, 16)}{a.check_out_time ? `–${a.check_out_time.slice(11, 16)}` : ''}
                      <Badge variant="secondary">{a.check_out_time ? 'done' : 'in pool'}</Badge>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={checkInOpen} onOpenChange={setCheckInOpen}>
        <DialogContent data-testid="checkin-dialog">
          <DialogHeader>
            <DialogTitle>Check In Member</DialogTitle>
            <DialogDescription>Search a member to check in. They must have an active membership or a confirmed booking.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <SwimmerSearch onSelect={checkIn} />
            {checking && <div className="flex items-center gap-2 mt-3 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Checking in…</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendancePage;
